import type { ListOrganizationUnitSummariesResponse } from '@springtree/eva-services-core';
import { cache } from 'react';
import { EVA_API_URL } from '@/core/constants/env';
import { filterOrganizationsForExpansionMarkets } from '@/core/constants/expansion-markets';
import {
  filterSupportedOrganizations,
  ORGANIZATION_UNIT_SET_ID,
  type OrganizationUnit,
} from '@/core/queries/eva/use-organizations-query';
import fetchWithResilience from '@/core/utils/fetch-with-resilience';
import timedFetch from '@/core/utils/ssr-timing';

const EVA_REVALIDATE_TIME = 3600;

async function fetchEvaOrganizationsFromApi(): Promise<OrganizationUnit[]> {
  const response = await fetchWithResilience(
    `${EVA_API_URL}/message/ListOrganizationUnitSummaries`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Eva-Ids-Mode': 'Hybrid',
        'Eva-User-Agent': 'Next-Middleware',
      },
      body: JSON.stringify({
        PageConfig: {
          Limit: 2000,
          Filter: {
            OrganizationUnitSetID: ORGANIZATION_UNIT_SET_ID,
          },
        },
      }),
      next: {
        revalidate: EVA_REVALIDATE_TIME,
        tags: ['eva'],
      },
    },
    { label: 'eva-organizations' },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch EVA organizations: ${response.status} ${response.statusText}`,
    );
  }

  const data = (await response.json()) as ListOrganizationUnitSummariesResponse;

  return filterOrganizationsForExpansionMarkets(
    filterSupportedOrganizations(data.Result.Page),
  );
}

/**
 * Canonical cached EVA organization list shared by proxy, layout, and pages.
 * Uses raw fetch (proxy-safe) with Next.js Data Cache and per-request dedup.
 */
const getCachedEvaOrganizations = cache(() =>
  timedFetch('eva-organizations', fetchEvaOrganizationsFromApi),
);

export default getCachedEvaOrganizations;
